import { AppService } from "@root/services";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNotification } from "@root/hooks/useNotification";
import { TOAST_VARIANT } from "@root/constants";
import { use } from "i18next";

type ThreadActionType = "pin" | "rename" | "delete";
type ThreadActionLoadingState = Record<
  number | string,
  Partial<Record<ThreadActionType, boolean>>
>;

export const useGlobal = () => {
  const { t } = useTranslation("common");
  const { showNotification } = useNotification();
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reports, setReports] = useState(null);
  const [reportCount, setReportCount] = useState(0);
  const [reportsLoadingExtraction, setReportsLoadingExtraction] =
    useState(false);
  const [reportsExtraction, setReportsExtraction] = useState(null);
  const [reportCountExtraction, setReportCountExtraction] = useState(0);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [threadActionLoading, setThreadActionLoading] =
    useState<ThreadActionLoadingState>({});
  const appService = useMemo(() => new AppService(), []);

  const getInitialSidebarState = () => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("sidebarOpen");
      if (saved !== null) {
        return JSON.parse(saved);
      }
      return window.innerWidth >= 768; // Default open for desktop
    }
    return true; // Safe default for SSR
  };

  const [isSidebarOpen, setIsSidebarOpen] = useState(getInitialSidebarState);

  const getReports = async (payload: any) => {
    try {
      setReportsLoading(true);
      const baseAuthUrl = process.env.REACT_APP_BASE_URL || "";
      const reports = await appService.getReports(baseAuthUrl, payload);
      setReports(reports);
      setReportCount(reports?.pagination?.totalItems);
    } catch (error) {
      console.log(error);
    } finally {
      setReportsLoading(false);
    }
  };
  //change api for extraction review only
  const getReportsExtraction = async (payload: any) => {
    try {
      setReportsLoadingExtraction(true);
      const baseAuthUrl = process.env.REACT_APP_BASE_URL || "";
      console.log("baseurl getReports", baseAuthUrl);
      const reports = await appService.viewReports(baseAuthUrl, payload);
      setReportsExtraction(reports?.rows);
      setReportCountExtraction(reports?.count);
    } catch (error) {
      console.log(error);
    } finally {
      setReportsLoadingExtraction(false);
    }
  };

  const updatePassword = async (payload: any) => {
    try {
      setResetPasswordLoading(true);
      const baseAuthUrl = process.env.REACT_APP_BASE_URL || "";
      const response = await appService.changePassword(baseAuthUrl, payload);
      console.log(response); //debug log
      setResetPasswordLoading(false);

      if (response === 1 || (Array.isArray(response) && response[0] === 1)) {
        return true;
      }
      return false;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const getBaseUrl = () => process.env.REACT_APP_BASE_URL || "";

  const updateThreadActionLoading = useCallback(
    (
      threadId: number | string,
      action: ThreadActionType,
      isLoading: boolean
    ) => {
      setThreadActionLoading((prev) => {
        const next = {
          ...prev,
          [threadId]: {
            ...(prev?.[threadId] || {}),
            [action]: isLoading,
          },
        };

        if (!isLoading) {
          const current = next[threadId];
          const hasActive = Object.values(current || {}).some(Boolean);
          if (!hasActive) {
            const { [threadId]: _, ...rest } = next;
            return rest;
          }
        }

        return next;
      });
    },
    []
  );

  const handleThreadAction = useCallback(
    async (
      threadId: number | string,
      action: ThreadActionType,
      request: () => Promise<any>,
      messages: { success: string; errorFallback?: string },
      callbacks?: { onSuccess?: () => void; onError?: (error?: any) => void }
    ) => {
      updateThreadActionLoading(threadId, action, true);
      try {
        await request();
        showNotification({
          type: TOAST_VARIANT.SUCCESS,
          message: messages.success,
        });
        callbacks?.onSuccess?.();
        return true;
      } catch (error: any) {
        const errorMessage =
          error?.statusText ||
          error?.data?.message ||
          messages?.errorFallback ||
          t("feedback.somethingWrong", {
            defaultValue: "Something went wrong",
          });
        showNotification({
          type: TOAST_VARIANT.ERROR,
          message: errorMessage,
        });
        callbacks?.onError?.(error);
        return false;
      } finally {
        updateThreadActionLoading(threadId, action, false);
      }
    },
    [showNotification, t, updateThreadActionLoading]
  );

  const toggleThreadPin = useCallback(
    async (
      threadId: number | string,
      callbacks?: { onSuccess?: () => void; onError?: (error?: any) => void }
    ) => {
      const baseAuthUrl = getBaseUrl();
      return handleThreadAction(
        threadId,
        "pin",
        () => appService.toggleThreadPin(baseAuthUrl, threadId),
        {
          success: t("chatHistory.pinToggleSuccess", {
            defaultValue: "Conversation pin updated.",
          }),
        },
        callbacks
      );
    },
    [appService, handleThreadAction, t]
  );

  const deleteThread = useCallback(
    async (
      threadId: number | string,
      callbacks?: { onSuccess?: () => void; onError?: (error?: any) => void }
    ) => {
      const baseAuthUrl = getBaseUrl();
      return handleThreadAction(
        threadId,
        "delete",
        () => appService.deleteThread(baseAuthUrl, threadId),
        {
          success: t("chatHistory.deleteSuccess", {
            defaultValue: "Conversation removed.",
          }),
        },
        callbacks
      );
    },
    [appService, handleThreadAction, t]
  );

  const renameThread = useCallback(
    async (
      threadId: number | string,
      name: string,
      callbacks?: { onSuccess?: () => void; onError?: (error?: any) => void }
    ) => {
      const baseAuthUrl = getBaseUrl();
      return handleThreadAction(
        threadId,
        "rename",
        () => appService.renameThread(baseAuthUrl, threadId, { name }),
        {
          success: t("chatHistory.renameSuccess", {
            defaultValue: "Conversation renamed.",
          }),
        },
        callbacks
      );
    },
    [appService, handleThreadAction, t]
  );

  return {
    t,
    isSidebarOpen,
    resetPasswordLoading,
    updatePassword,
    setIsSidebarOpen,
    getReports,
    reportCount,
    getReportsExtraction,
    reports,
    reportsLoading,
    toggleThreadPin,
    reportsExtraction,
    extractionReportsLoading: reportsLoadingExtraction,
    deleteThread,
    renameThread,
    threadActionLoading,
    reportCountExtraction,
  };
};
